import { useState, useEffect, useCallback } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  getAccessToken,
  getRefreshToken,
  isTokenExpired,
  refreshAccessToken,
  clearTokens,
} from '../lib/auth';

/**
 * Auth state type
 */
type AuthState = 'loading' | 'authenticated' | 'unauthenticated';

/**
 * Protected route component
 * Checks token validity and attempts refresh if expired
 * Redirects to login if user is not authenticated
 */
export function ProtectedRoute() {
  const [authState, setAuthState] = useState<AuthState>('loading');
  const location = useLocation();

  /**
   * Check authentication status
   * Verifies token and attempts refresh if needed
   */
  const checkAuthentication = useCallback(async () => {
    setAuthState('loading');

    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    // No tokens at all - not authenticated
    if (!accessToken && !refreshToken) {
      setAuthState('unauthenticated');
      return;
    }

    // Check if access token is valid (not expired)
    if (accessToken && !isTokenExpired(accessToken)) {
      setAuthState('authenticated');
      return;
    }

    // Access token is expired or missing, try to refresh
    if (refreshToken) {
      // Check if refresh token is also expired
      if (isTokenExpired(refreshToken, 0)) {
        // Refresh token is expired, clear everything
        clearTokens();
        setAuthState('unauthenticated');
        return;
      }

      // Attempt to refresh the access token
      try {
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          setAuthState('authenticated');
          return;
        }
      } catch {
        // Refresh failed, clear tokens
        clearTokens();
      }
    }

    // All attempts failed, user is not authenticated
    setAuthState('unauthenticated');
  }, []);

  // Check authentication on mount and when location changes
  useEffect(() => {
    checkAuthentication();
  }, [checkAuthentication]);

  // Show loading spinner while checking auth status
  if (authState === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
        role="status"
        aria-label="Checking authentication"
      >
        <CircularProgress size={48} aria-hidden="true" />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (authState === 'unauthenticated') {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render child routes if authenticated
  return <Outlet />;
}

export default ProtectedRoute;
